import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useAnimations - Hook for managing animations and micro-interactions
 * 
 * Features:
 * - Scroll-triggered animations
 * - Intersection Observer for performance
 * - Staggered animations
 * - Animation state management
 * - Reduced motion support
 */

export const useAnimations = () => {
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const elementRef = useRef(null);
  const observerRef = useRef(null);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setIsReducedMotion(mediaQuery.matches);

    const handleChange = (e) => setIsReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    if (!elementRef.current || isReducedMotion) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          // Disconnect after first intersection for performance
          observerRef.current?.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    observerRef.current.observe(elementRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [isReducedMotion]);

  // Animation utilities
  const addAnimation = useCallback((element, animationClass, duration = 300) => {
    if (!element || isReducedMotion) return;

    element.classList.add(animationClass);
    
    setTimeout(() => {
      element.classList.remove(animationClass);
    }, duration);
  }, [isReducedMotion]);

  const staggerAnimation = useCallback((elements, animationClass, staggerDelay = 100) => {
    if (!elements || isReducedMotion) return;

    elements.forEach((element, index) => {
      setTimeout(() => {
        addAnimation(element, animationClass);
      }, index * staggerDelay);
    });
  }, [addAnimation, isReducedMotion]);

  return {
    elementRef,
    isIntersecting,
    isReducedMotion,
    addAnimation,
    staggerAnimation
  };
};

/**
 * useScrollAnimations - Hook for scroll-triggered animations
 */
export const useScrollAnimations = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = true
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    if (!elementRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observerRef.current?.disconnect();
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observerRef.current.observe(elementRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { elementRef, isVisible };
};

/**
 * useStaggeredAnimation - Hook for staggered animations
 */
export const useStaggeredAnimation = (itemCount, delay = 100) => {
  const [visibleItems, setVisibleItems] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    if (!elementRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Stagger the visibility of items
          for (let i = 0; i < itemCount; i++) {
            setTimeout(() => {
              setVisibleItems(prev => Math.min(prev + 1, itemCount));
            }, i * delay);
          }
          observerRef.current?.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current.observe(elementRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [itemCount, delay]);

  return { elementRef, isVisible, visibleItems };
};

/**
 * useHoverAnimation - Hook for hover animations
 */
export const useHoverAnimation = (animationClass = 'hover-scale') => {
  const [isHovered, setIsHovered] = useState(false);
  const elementRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    if (elementRef.current) {
      elementRef.current.classList.add(animationClass);
    }
  }, [animationClass]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    if (elementRef.current) {
      elementRef.current.classList.remove(animationClass);
    }
  }, [animationClass]);

  return {
    elementRef,
    isHovered,
    handleMouseEnter,
    handleMouseLeave
  };
};

/**
 * useClickAnimation - Hook for click animations
 */
export const useClickAnimation = (animationClass = 'btn-press') => {
  const [isClicked, setIsClicked] = useState(false);
  const elementRef = useRef(null);

  const handleClick = useCallback((e) => {
    if (!elementRef.current) return;

    setIsClicked(true);
    elementRef.current.classList.add(animationClass);

    // Remove animation class after animation completes
    setTimeout(() => {
      elementRef.current?.classList.remove(animationClass);
      setIsClicked(false);
    }, 150);
  }, [animationClass]);

  return {
    elementRef,
    isClicked,
    handleClick
  };
};

/**
 * useRippleEffect - Hook for ripple effect on buttons
 */
export const useRippleEffect = () => {
  const elementRef = useRef(null);

  const createRipple = useCallback((e) => {
    if (!elementRef.current) return;

    const button = elementRef.current;
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;

    const ripple = document.createElement('span');
    ripple.style.cssText = `
      position: absolute;
      width: ${size}px;
      height: ${size}px;
      left: ${x}px;
      top: ${y}px;
      background: rgba(255, 255, 255, 0.3);
      border-radius: 50%;
      transform: scale(0);
      animation: ripple 0.6s linear;
      pointer-events: none;
    `;

    button.appendChild(ripple);

    setTimeout(() => {
      ripple.remove();
    }, 600);
  }, []);

  const handleClick = useCallback((e) => {
    createRipple(e);
  }, [createRipple]);

  return {
    elementRef,
    handleClick
  };
};

/**
 * useParallax - Hook for parallax scrolling effects
 */
export const useParallax = (speed = 0.5) => {
  const [offset, setOffset] = useState(0);
  const elementRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!elementRef.current) return;

      const rect = elementRef.current.getBoundingClientRect();
      const scrolled = window.pageYOffset;
      const rate = scrolled * -speed;
      
      setOffset(rate);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return {
    elementRef,
    offset
  };
};

/**
 * useTypingAnimation - Hook for typing animation effects
 */
export const useTypingAnimation = (text, speed = 100) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);

      return () => clearTimeout(timeout);
    } else {
      setIsComplete(true);
    }
  }, [currentIndex, text, speed]);

  const reset = useCallback(() => {
    setDisplayedText('');
    setCurrentIndex(0);
    setIsComplete(false);
  }, []);

  return {
    displayedText,
    isComplete,
    reset
  };
};

// CSS for ripple animation
export const rippleCSS = `
  @keyframes ripple {
    to {
      transform: scale(4);
      opacity: 0;
    }
  }
`;

export default {
  useAnimations,
  useScrollAnimations,
  useStaggeredAnimation,
  useHoverAnimation,
  useClickAnimation,
  useRippleEffect,
  useParallax,
  useTypingAnimation
};
